import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ── FAQ data ── */
const FAQ_DATA = [
  {
    category: "What is KYo Own?",
    items: [
      {
        q: "What is KYo Own?",
        a: "KYo Own is Jamaica's first micro-franchise courier programme. It gives everyday Jamaicans — teachers, taxi drivers, stay-at-home parents, retirees, young entrepreneurs — a fully packaged business to operate their own branded courier service from home. You focus on your customers and community. KYo Group handles everything else behind the scenes.",
      },
      {
        q: "Is this a job or a business?",
        a: "It is a business — your business. You are not an employee or a contractor. You are a KYo Own Partner: a micro-franchise operator with your own customer base, your own storefront, and your own earnings. KYo is your operational backbone, not your boss.",
      },
      {
        q: "What does KYo Own actually give me?",
        a: (
          <span>
            When you join KYo Own you receive a complete business-in-a-box, including:
            <ul className="mt-2 space-y-1 list-none">
              {[
                "A white-label ordering portal and landing page branded for your business",
                "The KYo Partner App or webpage with a live earnings dashboard",
                "Automated label creation and customs data collection tools",
                "A duty estimator your customers can use at checkout",
                "Brand assets – logo templates, promotional kits, and marketing graphics",
                "Access to ZipExpress pickup and delivery integration",
                "Partner training modules and dedicated customer care support",
                "Transparent weekly payouts – no hidden fees, no mysterious deductions",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ED2E24] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </span>
        ),
      },
    ],
  },
  {
    category: "How Does It Work?",
    items: [
      {
        q: "How does the KYo Own model work, step by step?",
        a: (
          <span>
            Think of it like operating your own courier company – but without the warehouse, the freight headaches, or the customs paperwork. Here is how the flow works:
            <ul className="mt-2 space-y-1 list-none">
              {[
                "Your customers shop online and place shipping orders through your branded KYo Own portal",
                "KYo handles receipt and storage of packages at our Miami warehouse",
                "KYo ships the packages to Jamaica and manages customs clearance at the airport or wharf",
                "Packages are delivered to one of KYo's five hub locations across the island",
                "You collect your customers' packages from your preferred hub",
                "You deliver to your customers in your community and collect your share of the fees",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-gray-600">
                  <span className="mt-0.5 text-[#ED2E24] font-bold text-xs shrink-0">{i + 1}.</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-gray-600">You earn by building relationships and growing your customer base – not by managing logistics.</p>
          </span>
        ),
      },
      {
        q: "What does KYo Group handle for me?",
        a: (
          <span>
            KYo takes responsibility for the entire backend operation:
            <ul className="mt-2 space-y-1 list-none">
              {[
                "Freight from the United States to Jamaica",
                "Customs clearance and duty processing",
                "Bonded warehousing and cargo consolidation",
                "Last-mile delivery from port to KYo hubs via ZipExpress",
                "Legal and customs compliance on every shipment",
                "Technology platform, tracking, and customer notifications",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ED2E24] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </span>
        ),
      },
      {
        q: "Where are the KYo hubs located?",
        a: "KYo operates warehouse and distribution hubs in Kingston, Montego Bay, and Mandeville, with additional hub locations across the island. You will collect your customers' packages from the hub closest and most convenient to your operating area.",
      },
      {
        q: "How quickly can I get started?",
        a: "The KYo Own sign-up process takes under ten minutes. Once onboarded and trained, you can begin accepting customers and taking orders within 24 hours.",
      },
    ],
  },
  {
    category: "Packages – What Is Facilitated?",
    items: [
      {
        q: "What types of packages does KYo Own handle?",
        a: "KYo Own facilitates packages of all sizes – from small envelopes and personal-care items to large boxes, electronics, appliances, and palletised freight. If it can be shipped from the United States to Jamaica, KYo can handle it.",
      },
      {
        q: "Are there any restrictions on what can be shipped?",
        a: "All shipments must comply with Jamaican Customs regulations and import restrictions. Items that are prohibited by law – such as certain chemicals, weapons, or controlled substances – cannot be shipped. KYo's compliance engine flags these automatically. When in doubt, contact the KYo team before accepting an order.",
      },
      {
        q: "How do package fees work?",
        a: "Fees are calculated based on the size and weight of each package. The larger or heavier the package, the higher the processing and delivery fees – and the greater your share of those earnings. This means that customers shipping bulkier or higher-value items represent your most lucrative orders.",
      },
      {
        q: "Does KYo handle customs duties for my customers?",
        a: "Yes. KYo's platform automatically calculates and manages customs duties on behalf of your customers. The duty estimator built into your ordering portal shows customers their total landed cost before they commit to an order – reducing surprises and building trust. Jamaica Customs requires an invoice for all packages; customers are reminded to attach invoices for every incoming shipment.",
      },
      {
        q: "What happens if a package is delayed or lost?",
        a: "KYo's system provides real-time tracking from the US warehouse through to hub collection. In the event of a delay, customers receive automated notifications. KYo's customer care team handles escalations, and micro-insurance options are available on shipments for added protection.",
      },
    ],
  },
  {
    category: "Your Operating Area",
    items: [
      {
        q: "Where am I allowed to operate?",
        a: "KYo Own partners agree to operate within a defined geographical area – your community, neighbourhood, parish, or professional network. This could be your street, your office building, your church community, your school, or your wider circle of friends and family.",
      },
      {
        q: "Why is operating in my community so important?",
        a: "The KYo Own model is built on trust. Your neighbours and network already know your name and face – that personal relationship is your most powerful business asset. Customers are far more comfortable giving a package to someone they know and can find easily. Concentrating on your community means better service, stronger retention, and a reputation that grows by word of mouth.",
      },
      {
        q: "Can I serve customers outside my agreed area?",
        a: "Your defined area is your primary zone of operation and is important for managing service quality and avoiding overlap with other KYo Own partners. If you want to expand your operating boundary, speak with the KYo Own team – growth is encouraged and tier upgrades are available as your business scales.",
      },
      {
        q: "Can I run KYo Own alongside my existing job or business?",
        a: "Absolutely. KYo Own is designed around your life, not the other way around. Whether you are a civil servant, a teacher, a taxi driver, or a stay-at-home parent, you decide when you work, how many customers you take on, and how fast you grow. There are no shifts, no clocking in, and no manager.",
      },
    ],
  },
  {
    category: "Earnings & Payouts",
    items: [
      {
        q: "How do I earn money as a KYo Own partner?",
        a: (
          <span>
            You retain a portion of the fees generated on every shipment your customers place. Your earnings come from a share of:
            <ul className="mt-2 space-y-1 list-none">
              {[
                "Freight fees",
                "Processing and service fees",
                "Duty handling fees",
                "Pickup and delivery fees",
                "Referral bonuses when you bring new partners or customers into the network",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ED2E24] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </span>
        ),
      },
      {
        q: "When and how do I get paid?",
        a: "KYo Own partners receive transparent weekly payouts directly through the Partner App or webpage. You can see exactly what you have earned on every transaction – no hidden fees and no unexpected deductions.",
      },
      {
        q: "How much can I earn?",
        a: "Your earnings depend on the number of customers you serve and the size and frequency of their shipments. Partners who start with 10 customers and grow to 200 see significantly higher weekly income. KYo Own offers tiered earning structures, loyalty bonuses, and partner events that reward growth – the bigger you build it, the more you earn.",
      },
      {
        q: "Are there startup costs to join KYo Own?",
        a: "KYo Own is designed to have a low barrier to entry. There are no warehouse costs, no truck or van requirements, and no large upfront capital outlay. Contact the KYo Own team for full details on the current partner onboarding package and any applicable fees.",
      },
    ],
  },
  {
    category: "Support & Technology",
    items: [
      {
        q: "What technology do I get access to?",
        a: (
          <span>
            Every KYo Own partner receives access to the KANAL technology platform, which includes:
            <ul className="mt-2 space-y-1 list-none">
              {[
                "A branded ordering portal your customers use to place shipments",
                "The KYo Partner App or webpage – earnings dashboard, parcel tracking, and customer management",
                "Automated customs data collection and label creation",
                "Real-time pickup, delivery, and customs status notifications",
                "ZipExpress mobile integration for last-mile dispatch and tracking",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ED2E24] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </span>
        ),
      },
      {
        q: "What training and support does KYo provide?",
        a: "KYo Own includes structured partner training modules covering onboarding, customer management, the ordering portal, and how to grow your customer base. Ongoing support is available through the KYo customer care escalation team. You are never left to figure things out alone.",
      },
      {
        q: "What if my customer has a complaint or problem?",
        a: "KYo's customer care team handles complaint escalations on your behalf. Your job is to be the trusted face your community relies on – KYo handles the backend resolution so that your reputation stays intact.",
      },
    ],
  },
];

/* ── Single accordion item ── */
function AccordionItem({ item, isOpen, onToggle, index }) {
  return (
    <div
      className={`border-b border-gray-100 last:border-0 transition-colors ${isOpen ? "bg-red-50/30" : "hover:bg-gray-50/60"}`}
      data-testid={`faq-item-${index}`}
    >
      <button
        className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left group"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${index}`}
      >
        <span className={`text-[15px] font-medium leading-snug transition-colors ${isOpen ? "text-[#ED2E24]" : "text-gray-900 group-hover:text-[#ED2E24]"}`}>
          {item.q}
        </span>
        <span className={`shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#ED2E24]" : "text-gray-400"}`}>
          <ChevronDown size={18} />
        </span>
      </button>

      {/* Animated answer panel */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? "1000px" : "0px", opacity: isOpen ? 1 : 0 }}
      >
        <div className="px-6 pb-5 text-[14px] text-gray-600 leading-relaxed border-l-2 border-[#ED2E24] ml-6 pl-4">
          {item.a}
        </div>
      </div>
    </div>
  );
}

/* ── Category section ── */
function CategorySection({ category, items, openIndex, onToggle, baseIndex }) {
  return (
    <div className="mb-8" data-testid={`faq-category-${category.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-[13px] font-bold uppercase tracking-widest text-[#ED2E24]">
          {category}
        </h2>
        <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
          {items.length}
        </span>
      </div>
      <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-sm bg-white">
        {items.map((item, i) => (
          <AccordionItem
            key={i}
            item={item}
            index={baseIndex + i}
            isOpen={openIndex === baseIndex + i}
            onToggle={() => onToggle(baseIndex + i)}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Page ── */
export default function FaqPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (idx) => setOpenIndex((prev) => (prev === idx ? null : idx));

  /* Flatten all items with their global index for filtering */
  const allItems = useMemo(() => {
    let idx = 0;
    return FAQ_DATA.map((cat) => ({
      ...cat,
      baseIndex: (() => { const b = idx; idx += cat.items.length; return b; })(),
    }));
  }, []);

  /* Filter by search query */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.q.toLowerCase().includes(q) ||
            (typeof item.a === "string" && item.a.toLowerCase().includes(q))
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query, allItems]);

  const totalVisible = filtered.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 pt-24 pb-20 px-5 lg:px-6">
        <div className="max-w-[760px] mx-auto">

          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-[#ED2E24] transition-colors mb-8"
            data-testid="faq-back-btn"
          >
            <ArrowLeft size={14} />
            Back
          </button>

          {/* Heading */}
          <div className="mb-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1a1a] leading-tight">
              Frequently Asked<br />Questions
            </h1>
            <div className="mt-3 w-12 h-1 bg-[#ED2E24] rounded-full" />
            <p className="mt-5 text-[15px] text-gray-500 leading-relaxed max-w-prose">
              Everything you need to know about KYo Own — Jamaica's first micro-franchise courier programme.
            </p>
          </div>

          {/* Search bar */}
          <div className="relative mb-10" data-testid="faq-search-container">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpenIndex(null); }}
              placeholder="Search questions…"
              className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3.5 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors shadow-sm placeholder:text-gray-400"
              data-testid="faq-search-input"
            />
            {query && (
              <button
                onClick={() => { setQuery(""); setOpenIndex(null); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full px-2 py-0.5"
                data-testid="faq-search-clear"
              >
                Clear
              </button>
            )}
          </div>

          {/* Result count when searching */}
          {query && (
            <p className="text-[13px] text-gray-400 mb-6" data-testid="faq-result-count">
              {totalVisible} result{totalVisible !== 1 ? "s" : ""} for &ldquo;{query}&rdquo;
            </p>
          )}

          {/* No results */}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400" data-testid="faq-no-results">
              <Search size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-[15px] font-medium">No questions matched &ldquo;{query}&rdquo;</p>
              <p className="text-[13px] mt-1">Try a different search term.</p>
            </div>
          )}

          {/* Categories */}
          {filtered.map((cat) => (
            <CategorySection
              key={cat.category}
              category={cat.category}
              items={cat.items}
              baseIndex={cat.baseIndex}
              openIndex={openIndex}
              onToggle={handleToggle}
            />
          ))}

          {/* CTA */}
          {!query && (
            <div className="mt-12 rounded-2xl bg-[#1a1a1a] px-8 py-10 text-center" data-testid="faq-cta">
              <h3 className="text-white text-[20px] font-semibold mb-2">Still have questions?</h3>
              <p className="text-gray-400 text-[14px] mb-6 max-w-sm mx-auto leading-relaxed">
                Our team is ready to help you get started with KYo Own.
              </p>
              <a
                href="https://kyoown.com"
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-semibold px-8 py-3 rounded-full transition-colors"
                data-testid="faq-cta-btn"
              >
                Contact KYo Own
              </a>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}
